// =====================================
// Real-time Notification Panel Component
// =====================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BellRing,
  X,
  Clock,
  AlertTriangle,
  Info,
  ShoppingCart,
  CreditCard,
  Users,
  ChefHat,
  WifiOff,
  Wifi,
  Circle
} from 'lucide-react';

import { useNotifications } from '@/hooks/useNotifications';
import { NotificationMessage } from '@/lib/api/notificationService';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils';
import { NavBell } from '@/assets/nav-icons';

interface NotificationPanelProps {
  className?: string;
  maxHeight?: number;
}

// Icon mapping for notification types
const getNotificationIcon = (type: string, priority: string) => {
  const iconClass = "w-5 h-5";
  
  switch (type) {
    case 'order_created':
      return <ShoppingCart className={iconClass} />;
    case 'order_ready':
      return <ChefHat className={iconClass} />;
    case 'payment_received':
      return <CreditCard className={iconClass} />;
    case 'table_status_changed':
      return <Users className={iconClass} />;
    case 'kitchen_update':
      return <ChefHat className={iconClass} />;
    case 'staff_alert':
      return priority === 'urgent' || priority === 'critical' 
        ? <AlertTriangle className={iconClass} />
        : <Info className={iconClass} />;
    case 'system_alert':
      return <Info className={iconClass} />;
    default:
      return <NavBell className={iconClass} />;
  }
};

// Color mapping for notification priorities
const getPriorityColors = (priority: string) => {
  switch (priority) {
    case 'critical':
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: 'text-red-600',
        dot: 'bg-red-500'
      };
    case 'urgent':
      return {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-800',
        icon: 'text-orange-600',
        dot: 'bg-orange-500'
      };
    case 'high':
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: 'text-yellow-600',
        dot: 'bg-yellow-500'
      };
    case 'normal':
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: 'text-blue-600',
        dot: 'bg-blue-500'
      };
    case 'low':
    default:
      return {
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-800',
        icon: 'text-gray-600',
        dot: 'bg-gray-500'
      };
  }
};

// Individual notification item component
const NotificationItem: React.FC<{
  notification: NotificationMessage;
  onRemove: (id: string) => void;
}> = ({ notification, onRemove }) => {
  const { t } = useTranslation();
  const colors = getPriorityColors(notification.priority);
  
  const timeAgo = React.useMemo(() => {
    const now = new Date();
    const notificationTime = new Date(notification.timestamp);
    const diffMs = now.getTime() - notificationTime.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) return t('notifications.just_now', 'только что');
    if (diffMinutes < 60) return t('notifications.minutes_ago', '{{count}} мин назад', { count: diffMinutes });
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return t('notifications.hours_ago', '{{count}} ч назад', { count: diffHours });
    
    const diffDays = Math.floor(diffHours / 24);
    return t('notifications.days_ago', '{{count}} д назад', { count: diffDays });
  }, [notification.timestamp, t]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={cn(
        'p-4 border rounded-lg transition-all hover:shadow-md',
        colors.bg,
        colors.border
      )}
    >
      <div className="flex items-start space-x-3">
        {/* Priority dot */}
        <div className="flex-shrink-0 mt-1">
          <Circle className={cn('w-2 h-2 fill-current', colors.dot)} />
        </div>
        
        {/* Notification icon */}
        <div className={cn('flex-shrink-0 mt-0.5', colors.icon)}>
          {getNotificationIcon(notification.type, notification.priority)}
        </div>
        
        {/* Notification content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <h4 className={cn('font-medium text-sm', colors.text)}>
              {notification.title}
            </h4>
            <button
              onClick={() => onRemove(notification.id)}
              className="flex-shrink-0 p-1 hover:bg-white hover:bg-opacity-50 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{timeAgo}</span>
            </div>
            
            {notification.senderRole !== 'system' && (
              <span className="text-xs text-gray-500">
                {notification.senderName}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Connection status indicator
const ConnectionStatus: React.FC<{
  isConnected: boolean;
  isAuthenticated: boolean;
}> = ({ isConnected, isAuthenticated }) => {
  const { t } = useTranslation();
  
  if (isConnected && isAuthenticated) {
    return (
      <div className="flex items-center space-x-2 text-green-600">
        <Wifi className="w-4 h-4" />
        <span className="text-xs">{t('notifications.connected', 'Подключено')}</span>
      </div>
    );
  } else if (isConnected && !isAuthenticated) {
    return (
      <div className="flex items-center space-x-2 text-yellow-600">
        <AlertTriangle className="w-4 h-4" />
        <span className="text-xs">{t('notifications.authenticating', 'Авторизация...')}</span>
      </div>
    );
  } else {
    return (
      <div className="flex items-center space-x-2 text-red-600">
        <WifiOff className="w-4 h-4" />
        <span className="text-xs">{t('notifications.disconnected', 'Отключено')}</span>
      </div>
    );
  }
};

// Main notification panel component
export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  className,
  maxHeight = 400
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const {
    isConnected,
    isAuthenticated,
    notifications,
    unreadCount,
    error,
    clearNotifications,
    markAsRead,
    removeNotification,
    connect,
    disconnect,
    ping,
    getConnectionInfo
  } = useNotifications({
    maxNotifications: 50,
    autoConnect: true,
    showToast: false, // We'll handle notifications in the panel
  });

  const handleTogglePanel = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      markAsRead(); // Mark all as read when opening panel
    }
  };

  const handleClearAll = () => {
    clearNotifications();
  };

  const handleReconnect = () => {
    disconnect();
    setTimeout(connect, 1000);
  };

  return (
    <div className={cn('relative', className)}>
      {/* Notification bell button */}
      <button
        onClick={handleTogglePanel}
        className={cn(
          'relative grid h-11 w-11 place-items-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
          isOpen 
            ? 'bg-primary-100 text-primary-600' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        )}
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5" />
        ) : (
          <NavBell className="w-5 h-5" />
        )}
        
        {/* Unread count badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-sm flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
            style={{ maxHeight }}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">
                  {t('notifications.title', 'Уведомления')}
                </h3>
                <div className="flex items-center space-x-2">
                  {error && (
                    <button
                      onClick={handleReconnect}
                      className="text-xs text-red-600 hover:text-red-800 underline"
                      title={t('notifications.reconnect', 'Переподключиться')}
                    >
                      {t('notifications.retry', 'Повтор')}
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <ConnectionStatus 
                  isConnected={isConnected} 
                  isAuthenticated={isAuthenticated} 
                />
                
                {notifications.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAll}
                    className="text-xs py-1 px-2"
                  >
                    {t('notifications.clear_all', 'Очистить все')}
                  </Button>
                )}
              </div>
              
              {error && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                  {error}
                </div>
              )}
            </div>

            {/* Notifications list */}
            <div 
              className="overflow-y-auto"
              style={{ maxHeight: maxHeight - 120 }}
            >
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <NavBell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {t('notifications.empty', 'Нет уведомлений')}
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  <AnimatePresence>
                    {notifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onRemove={removeNotification}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {t('notifications.total_count', '{{count}} уведомлений', { count: notifications.length })}
                </span>
                <button
                  onClick={() => {
                    ping();
                    console.log('Connection info:', getConnectionInfo());
                  }}
                  className="hover:text-gray-700"
                >
                  {t('notifications.test_connection', 'Тест связи')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};